import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  ViewChild,
  SimpleChanges,
  ChangeDetectorRef,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  viewChild,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  CommunicationUserType,
  InputType,
  ModalQueryParam,
  NotificationTypes,
  SuggestionFormStep,
  SuggestionVisibility,
} from 'src/app/models/enums';
import {
  NotificationService,
  StorageService,
  UsersService,
  ClientsService,
  TripsService,
  UtilsService,
  ModalUrlService,
} from 'src/app/services';
import { ModalConversationComponent } from 'src/app/shared/modals/modal-conversation/modal-conversation.component';
import { LiteralService } from 'src/app/services/literal/literal.service';
import { ServiceInfoService } from 'src/app/services/service-info/service-info.service';
import { TooltipPosition } from 'src/app/shared/tooltip/tooltip-position';
import { images } from 'src/images';
import { ISuggestion } from 'src/app/models/suggestion';
import {
  getSuggestionForm,
  prepareSuggestionClient,
  UserSuggestionData,
} from 'src/app/forms/suggestionForms';
import { Select2ScrollEvent, Select2SearchEvent } from 'ng-select2-component';
import { SuggestionService } from 'src/app/services/suggestion/suggestion.service';
import { Modal } from 'flowbite';
import { Subscription } from 'rxjs';
import { NavigationStart, Router } from '@angular/router';
import { MessageReceiver } from 'src/app/models/enums/messageReceiver';
import { IExpeditionSuggestionData } from 'src/app/forms/suggestionData';
import {
  isRaisingVisibilityToPassengers,
  isVisibleToPassengers,
  suggestionTypeIcon,
  suggestionUserResolutionBadgeClass,
} from 'src/app/utils/suggestionTickets';

@Component({
  selector: 'app-suggestion-details',
  templateUrl: './suggestion-details.component.html',
  styleUrls: ['./suggestion-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SuggestionDetailsComponent
  implements OnInit, AfterViewInit, OnChanges, OnDestroy
{
  @Input() userType: CommunicationUserType;
  @Input() suggestion: any = {} as ISuggestion;
  @Input() newSuggestion: boolean;
  @Input() editSuggestion: boolean;
  @Input() shouldOpenConversation: boolean = false;
  @Input() startStep?: SuggestionFormStep;
  @Output() closeDetails = new EventEmitter();
  @Output() suggestionEdited = new EventEmitter<ISuggestion>();

  @ViewChild('form') form: ElementRef;

  public originalSuggestion: any = {} as ISuggestion;
  public anonymousUser: boolean = false;

  public TooltipPosition = TooltipPosition;
  public InputType = InputType;
  public CommunicationUserType = CommunicationUserType;
  public images = images;

  public expanded = false;
  public readonly showMessagesToUserButton = false;
  public edit = false;
  public shownSuggestionId?: number;

  public steps = [1];
  public currentStep: number = 1;
  private startStepApplied = false;
  private pendingFormUpdate: Promise<void> = Promise.resolve();
  public numberSteps: number = 3;

  public suggestionForm: any[];
  public suggestionFormControl: FormGroup;
  public invalid: any[] = [];

  public clients: any;
  public serviceInfos: any;

  public conversation: any[] = [];
  public conversationModal: Modal;
  public conversationModalShowed = false;
  private readonly conversationModalComponent = viewChild(ModalConversationComponent);

  public publicVisibilityModal: Modal;
  public publicVisibilityModalShowed = false;

  public suggestionUserResolutionBadgeClass = suggestionUserResolutionBadgeClass;

  public routerSubscription: Subscription;

  constructor(
    public literalService: LiteralService,
    public usersService: UsersService,
    public utilsService: UtilsService,
    public notificationService: NotificationService,
    public storageService: StorageService,
    private suggestionService: SuggestionService,
    private clientService: ClientsService,
    private serviceInfoService: ServiceInfoService,
    private tripsService: TripsService,
    private changeDetectorRef: ChangeDetectorRef,
    private router: Router,
    private modalUrlService: ModalUrlService,
  ) {}

  ngOnInit(): void {
    this.routerSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.closeModals();
      }
    });
  }

  ngAfterViewInit() {
    if (!this.newSuggestion && this.suggestion)
      this.shownSuggestionId = this.suggestion.id;
  }

  async ngOnChanges(changes?: SimpleChanges) {
    if (changes?.['newSuggestion']?.previousValue && !changes['newSuggestion'].currentValue) this.resetWizard();
    if (this.newSuggestion) {
      this.suggestionForm = [];
      this.shownSuggestionId = undefined;
    } else if (
      this.suggestion &&
      this.suggestion.id != this.shownSuggestionId
    ) {
      this.shownSuggestionId = this.suggestion.id;
      this.originalSuggestion = structuredClone(this.suggestion);
    } else if (!this.suggestion) {
      if (this.expanded) this.expanded = false;
    }

    if (
      changes &&
      changes['editSuggestion'] &&
      changes['editSuggestion'].currentValue
    ) {
      this.edit = changes['editSuggestion'].currentValue;
      this.originalSuggestion = structuredClone(this.suggestion);
    }

    // Open conversation modal when shouldOpenConversation is true
    if (
      changes &&
      changes['shouldOpenConversation']?.currentValue &&
      this.canOpenConversation() &&
      !this.newSuggestion
    ) {
      setTimeout(() => this.openConversation(), 100);
    }

    if (
      changes &&
      changes['editSuggestion'] &&
      changes['editSuggestion'].currentValue
    ) {
      this.edit = changes['editSuggestion'].currentValue;
      this.originalSuggestion = structuredClone(this.suggestion);
    }

    this.suggestion && this.updateSuggestionForm(this.newSuggestion ? new UserSuggestionData({}) : undefined);
    this.changeDetectorRef.detectChanges();
  }

  closeModals() {
    if (this.conversationModalShowed) this.conversationModal.hide();
    if (this.publicVisibilityModalShowed) this.publicVisibilityModal.hide();
  }

  getDetailIcon() {
    return suggestionTypeIcon(this.suggestion?.type);
  }

  getDetailTitle() {
    if (this.newSuggestion) return 'new';
    if (this.suggestion) return this.suggestion.type.toLowerCase();
    return '';
  }

  setAnonymousUser() {
    this.anonymousUser = !this.anonymousUser;
    if (this.anonymousUser) {
      this.suggestion.userSuggester = {
        firstName: this.literalService.get('suggestions.anonymousUser', true),
        lastName: '',
      };
    } else {
      this.suggestion.userSuggester = null;
    }
    this.updateSuggestionForm();
  }

  updateSuggestionForm(
    userSuggestionData?: UserSuggestionData,
    changes = [''],
    step?: number,
    expeditionData?: IExpeditionSuggestionData,
  ): Promise<void> {
    this.pendingFormUpdate = this.pendingFormUpdate
      .catch(() => undefined)
      .then(() => this.rebuildSuggestionForm(userSuggestionData, changes, step, expeditionData));
    return this.pendingFormUpdate;
  }

  private async rebuildSuggestionForm(
    userSuggestionData: UserSuggestionData | undefined,
    changes: string[],
    step: number | undefined,
    expeditionData?: IExpeditionSuggestionData,
  ) {
    if (this.newSuggestion && this.startStep && !this.startStepApplied) {
      this.startStepApplied = true;
      this.numberSteps = await prepareSuggestionClient(this.usersService, this.suggestion, true);
      const targetStep = this.reachableStartStep(this.startStep);
      if (targetStep > this.currentStep) {
        this.currentStep = targetStep;
        this.steps = Array.from({ length: targetStep }, (_, index) => index + 1);
        step = undefined;
      }
    }
    await this.buildSuggestionForm(userSuggestionData, changes, step, expeditionData);
    this.getGridRows();
  }

  private reachableStartStep(startStep: SuggestionFormStep): number {
    const locationStep = this.numberSteps - 1;
    if (!this.suggestion.client?.id) return 1;
    if (startStep === SuggestionFormStep.DETAILS && this.suggestion.town?.id && this.suggestion.userSuggester?.id)
      return this.numberSteps;
    return startStep === SuggestionFormStep.CLIENT ? 1 : locationStep;
  }

  private async buildSuggestionForm(
    userSuggestionData: UserSuggestionData | undefined,
    changes: string[],
    step: number | undefined,
    expeditionData?: IExpeditionSuggestionData,
  ) {
    this.suggestionForm = await getSuggestionForm(
      this.suggestion,
      this.userType,
      this.literalService,
      this.usersService,
      this.storageService,
      this.clientService,
      userSuggestionData,
      changes,
      this.edit,
      this.newSuggestion,
      step ?? this.currentStep,
      this.serviceInfoService,
      {
        tripsService: this.tripsService,
        expeditionData,
      },
    );
    this.numberSteps = Math.max(
      ...this.suggestionForm.flatMap((element) => element.step || 0),
    );
  }

  getGridRows() {
    const formContainer = document.getElementById('suggestionDetailForm');
    if (!formContainer) return;
    if (!this.newSuggestion) {
      formContainer.style.gridTemplateRows = '';
      return;
    }
    const visibleElements = this.suggestionForm.filter((data) => !data.hidden);
    const longTexts = visibleElements.filter((data) => data.edit.inputType === InputType.LONG_TEXT).length;
    formContainer.style.gridTemplateRows = `repeat(${visibleElements.length + longTexts}, auto)`;
  }

  async changeData(data: any, element?: any) {
    if (data !== undefined && data !== '') {
      const valueToChange = element?.edit.valueToChange;

      switch (valueToChange) {
        case 'user':
          this.suggestion.userSuggester = data;
          if (this.anonymousUser) this.anonymousUser = false;
          //this.updateSuggestionForm(undefined, ['user']);
          break;
        case 'town':
          this.suggestion[valueToChange] = data;
          this.suggestion.service = undefined;
          this.updateSuggestionForm();
          break;
        case 'service':
          this.suggestion.service = data;
          this.suggestion.expedition = null;
          this.updateSuggestionForm();
          break;
        case 'visibility':
          this.suggestion.visibility = data;
          this.updateSuggestionForm();
          break;
        case 'client':
        case 'expedition':
        case 'affair':
        case 'status':
        case 'priority':
          this.suggestion[valueToChange] = data;
          break;
        case 'type':
          this.suggestion[valueToChange] = data;
          this.updateSuggestionForm();
          break;
        default:
          this.suggestion[valueToChange] = data.value;
          break;
      }
    }
  }

  async scroll(event: Select2ScrollEvent, valueToChange?: string) {
    if (valueToChange === 'expedition') {
      if (event.way === 'down')
        this.updateSuggestionForm(undefined, undefined, undefined, { showMore: true });
      return;
    }
    if (event.way === 'down') {
      if (event.search !== '') {
        this.updateSuggestionForm(
          new UserSuggestionData({
            showMoreUsers: true,
            search: true,
            value: event.search,
          }),
        );
      } else {
        this.updateSuggestionForm(
          new UserSuggestionData({ showMoreUsers: true, search: false }),
        );
      }
    }
  }

  async search(event: Select2SearchEvent, valueToChange: string) {
    if (valueToChange === 'expedition') {
      this.updateSuggestionForm(undefined, undefined, undefined, {
        showMore: false,
        search: event.search,
      });
      return;
    }
    if (valueToChange === 'user') {
      const dropDown = document.getElementsByClassName(
        'select2-results__options',
      )[0];
      this.updateSuggestionForm(
        new UserSuggestionData({
          showMoreUsers: false,
          search: true,
          value: event.search,
        }),
      );
      dropDown.scroll({
        top: 0,
      });
    }
  }

  continue() {
    if (!this.steps.includes(2) && this.currentStep === 1) {
      !this.steps.includes(2) && this.steps.push(2);
      this.currentStep = 2;
      this.updateSuggestionForm(
        new UserSuggestionData({ showMoreUsers: false }),
        ['client'],
      );
    } else if (!this.steps.includes(3) && this.currentStep === 2) {
      !this.steps.includes(3) && this.steps.push(3);
      this.currentStep = 3;
      this.suggestion && this.updateSuggestionForm();
    }
    if (this.currentStep === this.numberSteps) {
      this.scrollToBottom();
    }
  }

  private scrollToBottom() {
    setTimeout(() => {
      const container = document.querySelector(
        '#suggestionsDetailContainer .scrollable-content',
      );
      if (container) {
        container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
      }
    });
  }

  async goBack() {
    await this.updateSuggestionForm(undefined, undefined, this.currentStep - 1);
    this.currentStep = this.currentStep - 1;
    this.steps.pop();
  }

  editAction(event: any) {
    event.stopPropagation();
    this.edit = true;
    this.originalSuggestion = structuredClone(this.suggestion);
    this.updateSuggestionForm();
  }

  public isValidDate(d: any) {
    return d instanceof Date && !isNaN(Number(d));
  }

  public close() {
    this.newSuggestion = false;
    this.resetWizard();
    this.closeDetails.emit();
  }

  private resetWizard() {
    this.expanded = false;
    this.edit = false;
    this.anonymousUser = false;
    this.currentStep = 1;
    this.numberSteps = 3;
    this.steps = [1];
    this.startStepApplied = false;
  }

  cancel(event: any) {
    event.stopPropagation();
    if (this.edit) {
      this.edit = false;
      this.suggestion = structuredClone(this.originalSuggestion);
      this.updateSuggestionForm();
    } else this.close();
  }

  public initFormControl() {
    if (this.newSuggestion) {
      if (this.numberSteps === 3 && this.currentStep === 1) {
        this.suggestionFormControl = new FormGroup({
          client: new FormControl(this.suggestion.client, [
            Validators.required,
          ]),
        });
      } else if (this.currentStep === this.numberSteps - 1) {
        this.suggestionFormControl = new FormGroup({
          town: new FormControl(this.suggestion.town, [Validators.required]),
          userSuggester: new FormControl(this.suggestion.userSuggester, [
            Validators.required,
          ]),
        });
      } else {
        this.suggestionFormControl = new FormGroup({
          type: new FormControl(this.suggestion.type, [Validators.required]),
          affair: new FormControl(this.suggestion.affair, [
            Validators.required,
          ]),
          description: new FormControl(this.suggestion.description, [
            Validators.required,
          ]),
          priority: new FormControl(this.suggestion.priority, [
            Validators.required,
          ]),
          status: new FormControl(this.suggestion.status, [
            Validators.required,
          ]),
          visibility: new FormControl(this.suggestion.visibility, [
            Validators.required,
          ]),
          publicMessage: this.publicMessageControl(),
        });
      }
    } else {
      this.suggestionFormControl = new FormGroup({
        priority: new FormControl(this.suggestion.priority, [
          Validators.required,
        ]),
        status: new FormControl(this.suggestion.status, [Validators.required]),
        comments: new FormControl(this.suggestion.comments, [
          Validators.required,
        ]),
        publicMessage: this.publicMessageControl(),
      });
    }
  }

  private publicMessageControl() {
    return new FormControl(
      this.suggestion.publicMessage,
      isVisibleToPassengers(this.suggestion.visibility) ? [Validators.required] : [],
    );
  }

  public async findInvalidControls() {
    this.initFormControl();
    this.invalid = [];
    const controls = this.suggestionFormControl.controls;
    for (const name in controls) {
      if (controls[name].invalid && name != 'comments') {
        this.invalid.push(name);
      }
    }
  }

  showError(value: string, field: string) {
    let errorText = '';
    this.invalid.map((invalidField: string) => {
      if (invalidField === field) {
        if (
          !this.suggestion[invalidField] ||
          this.suggestion[invalidField].length === 0
        ) {
          if (invalidField === 'email' || invalidField === 'phone')
            errorText = this.literalService.get('requiredFieldOrNext', true);
          else
            errorText = this.literalService.get('errors.requiredField', true);
        } else {
          errorText = this.literalService.get('errors.incorrectField', true);
        }
      }
    });
    return errorText;
  }

  resetSuggestionInfo() {
    this.edit = false;
    this.newSuggestion = false;
    this.suggestionEdited.emit(this.suggestion);
    this.originalSuggestion = structuredClone(this.suggestion);
    this.ngOnChanges();
  }

  async onSubmit() {
    await this.findInvalidControls();
    if (this.invalid.length > 0) return;
    const previousVisibility = this.edit
      ? this.originalSuggestion?.visibility
      : SuggestionVisibility.PRIVATE;
    if (isRaisingVisibilityToPassengers(previousVisibility, this.suggestion.visibility)) {
      this.showPublicVisibilityModal();
      return;
    }
    await this.saveSuggestion();
  }

  showPublicVisibilityModal() {
    this.publicVisibilityModal = new Modal(
      document.getElementById('publicVisibilityModal'),
      {
        placement: 'center',
        closable: true,
        onHide: () => (this.publicVisibilityModalShowed = false),
        onShow: () => (this.publicVisibilityModalShowed = true),
      },
    );
    this.publicVisibilityModal.toggle();
  }

  async confirmPublicVisibility() {
    this.publicVisibilityModal.hide();
    await this.saveSuggestion();
  }

  async saveSuggestion() {
    try {
      if (this.edit) {
        this.suggestion = await this.suggestionService.updateSuggestion(
          this.suggestion,
        );
      } else {
        this.suggestion = await this.suggestionService.createSuggestion(
          this.suggestion,
        );
      }
      this.utilsService.showNotification(
        images.notifications.check,
        `suggestions.actions.${this.edit ? 'edit' : 'create'}`,
        NotificationTypes.SUCCESS,
      );
      this.resetSuggestionInfo();
      this.close();
    } catch {
      this.utilsService.showNotification(
        images.notifications.warning,
        `suggestions.actions.${this.edit ? 'editError' : 'createError'}`,
        NotificationTypes.DANGER,
      );
    }
  }

  public isExpandedFormInvalid(invalidArray: string[]) {
    return (
      this.suggestionForm &&
      this.suggestionForm.some(
        (form) =>
          form.expanded &&
          form.edit &&
          form.edit.valueToChange &&
          invalidArray.includes(form.edit.valueToChange),
      )
    );
  }

  openConversation() {
    const conversationModalComponent = this.conversationModalComponent();
    if (!conversationModalComponent || this.conversationModalShowed) return;
    this.conversationModal = new Modal(conversationModalComponent.modalContainer().nativeElement, {
      closable: false,
      onHide: () => {
        this.conversationModalShowed = false;
        this.modalUrlService.markClosed(ModalQueryParam.CONVERSATION);
      },
      onShow: () => (this.conversationModalShowed = true),
    });
    this.conversation = this.suggestion.conversation;
    this.conversationModal.show();
    this.shouldOpenConversation = false;
    this.modalUrlService.markOpen(ModalQueryParam.CONVERSATION);
  }

  canOpenConversation() {
    return !!this.suggestion?.userSuggester?.id;
  }

  closeConversation() {
    this.conversation = [];
    this.conversationModal.hide();
  }

  sendMessage(newMessage: string) {
    this.suggestionService
      .sendConversationMessage(this.suggestion.id, newMessage)
      .then((res: any) => {
        this.suggestion.conversation = structuredClone(res.conversation);
        this.conversation = structuredClone(res.conversation);
      });
  }

  async goToUserMessages() {
    const messageFilters = await this.storageService.getMessagesFilters();
    let newMessageFilters: any = structuredClone(JSON.parse(messageFilters));
    newMessageFilters = {
      ...newMessageFilters,
      receivers: {
        selectedReceiver:
          this.userType === CommunicationUserType.DRIVERS
            ? MessageReceiver.DRIVER
            : MessageReceiver.USER,
        selectedReceivers: [this.suggestion.userSuggester],
      },
    };
    this.storageService.setMessagesFilters(JSON.stringify(newMessageFilters));
    this.router.navigate([`/messages/${this.userType.toLowerCase()}`]);
  }

  disabledSaveButton() {
    if (this.edit && this.suggestion && this.originalSuggestion)
      return (
        this.utilsService.compareObjects(
          this.suggestion,
          this.originalSuggestion,
        ).length === 0
      );
    if (this.suggestionForm) {
      this.initFormControl();
      return !this.suggestionFormControl.valid;
    }
    return true;
  }

  ngOnDestroy(): void {
    this.closeModals();
    this.routerSubscription?.unsubscribe();
  }
}
